import { css } from '@emotion/react';
import { gray2, gray5 } from 'styles/global_defaults/colors';
import {
  extraLargeSpacing,
  largeSpacing,
  standardSpacing,
  threeQuartersSpacing,
} from 'styles/global_defaults/scaffolding';
import {
  headerLineHeight,
  textMediumFontSize,
  textSmallLineHeight,
} from 'styles/global_defaults/fonts';
import { PulseCheckQuestion } from 'redux/schemas/models/mentoring-program-sessions';
import { useFormContext } from 'react-hook-form';
import { config } from '@config/pendo.config.json';
import LikertScaleQuestion from './questions/likert-scale-question';
import TextQuestion from './questions/text-question';
import { sortByQuestionIndex } from './utils';

enum PulseCheckQuestionType {
  LIKERT_SCALE = 'LikertScaleQuestion',
  LONG_TEXT = 'LongTextQuestion',
}

type PulseCheckQuestionsProps = {
  questions: PulseCheckQuestion[];
};

const styles = css`
  .likert-question {
    padding: ${largeSpacing}px ${threeQuartersSpacing}px;

    .question-title {
      line-height: ${headerLineHeight}px;
    }
    .question-scale-label {
      color: ${gray2};
      align-content: flex-end;
      margin-bottom: ${standardSpacing}px;
      line-height: ${textSmallLineHeight}px;
    }

    .rc-slider-mark-text {
      top: -${extraLargeSpacing}px !important;
      font-size: ${textMediumFontSize}px !important;
    }
  }
  .divider {
    border-bottom: 1px solid ${gray5};
  }
`;

const PulseCheckQuestions = ({ questions }: PulseCheckQuestionsProps) => {
  const { setValue } = useFormContext();
  const dataQATag = config.pendo.athena.mentorshipProgram.participantHome.sessionPulseCheck
    .questionType;
  const questionTypes = {
    [PulseCheckQuestionType.LIKERT_SCALE]: LikertScaleQuestion,
    [PulseCheckQuestionType.LONG_TEXT]: TextQuestion,
  };

  const pulseCheckQuestions: (PulseCheckQuestion & {
    type: PulseCheckQuestionType;
    showDivider?: boolean;
    dataQA?: string;
    dataQAId?: string;
  })[] = [...(questions || [])]
    .sort(sortByQuestionIndex)
    .map((questionItem) => ({
      ...questionItem,
      type:
        questionItem.questionIndex <= 2
          ? PulseCheckQuestionType.LIKERT_SCALE
          : PulseCheckQuestionType.LONG_TEXT,
      showDivider: questionItem.questionIndex <= 1,
      dataQA:
        questionItem.questionIndex <= 2
          ? `${dataQATag.likertScale}_${questionItem.questionIndex}`
          : dataQATag.textInput,
      dataQAId:
        questionItem.questionIndex <= 2
          ? `${dataQATag.likertScale}_${questionItem.questionIndex}_${questionItem.id}`
          : `${dataQATag.textInput}`,
    }));

  const onPulseCheckResponse = (
    questionIndex: number,
    questionId: number,
    response: number | string,
  ) => {
    setValue(
      `pulseCheckResponses[${questionIndex}]`,
      { questionIndex, questionId, response },
      { shouldValidate: true, shouldDirty: true },
    );
  };

  return (
    <div css={styles} className='d-flex flex-column'>
      {pulseCheckQuestions.map(
        ({
          id,
          type,
          questionIndex,
          questionText,
          showDivider = false,
          dataQA,
          dataQAId,
        }) => {
          const PulseCheckQuestionItem = questionTypes[type];
          return (
            <PulseCheckQuestionItem
              key={id}
              questionId={id}
              questionIndex={questionIndex}
              questionText={questionText}
              showDivider={showDivider}
              onResponse={onPulseCheckResponse}
              dataQA={dataQA}
              dataQAId={dataQAId}
            />
          );
        },
      )}
    </div>
  );
};

export default PulseCheckQuestions;
